import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useMemo,
} from "react";
import newYearAudio from "../assets/audio/event_song.mp3";
import { useNewYear2025Theme } from "../helper/const";
import { useLocation } from "react-router";

const NewYearAudioContext = createContext();

const AUDIO_SETTINGS_KEY = "miniGameAudioSettings";

// Helper functions for localStorage
const getStoredAudioSettings = () => {
  try {
    const stored = localStorage.getItem(AUDIO_SETTINGS_KEY);
    return stored ? JSON.parse(stored) : { isPlaying: true };
  } catch (error) {
    console.error("Error reading audio settings:", error);
    return { isPlaying: true };
  }
};

const setStoredAudioSettings = (settings) => {
  try {
    localStorage.setItem(AUDIO_SETTINGS_KEY, JSON.stringify(settings));
  } catch (error) {
    // console.error("Error saving audio settings:", error);
  }
};

export const NewYearAudioProvider = ({ children }) => {
  const [isPlaying, setIsPlaying] = useState(() => {
    // Initialize from localStorage
    const stored = getStoredAudioSettings();
    return stored.isPlaying;
  });
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const audioRef = useRef(null);
  const hasAutoPlayedRef = useRef(false);
  const IS_EVENT_NEW_YEAR_2025_THEME = useNewYear2025Theme();
  const location = useLocation();
  const events = useMemo(() => [
    "click",
    "mousedown",
    "mouseup",
    "touchstart",
    "touchend",
    "touchmove",
    "keydown",
    "scroll",
  ], []);
  
  const isOnMiniGamePath = location.pathname.includes("/mini-game");

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.5;
    }
  }, []);

  // Auto-play effect when entering minigame route
  useEffect(() => {
    if (!isOnMiniGamePath || !IS_EVENT_NEW_YEAR_2025_THEME) {
      hasAutoPlayedRef.current = false;
      return;
    }

    // Only attempt auto-play once per route entry
    if (hasAutoPlayedRef.current) return;

    const stored = getStoredAudioSettings();
    if (stored.isPlaying && audioRef.current) {
      // Set a small delay to ensure audio element is ready
      const timer = setTimeout(() => {
        audioRef.current?.play()
          .then(() => {
            console.log("Audio auto-play successful");
            setUserHasInteracted(true); // Mark as interacted since auto-play worked
          })
          .catch((error) => {
            // This is expected on page load - browser blocks auto-play until user interaction
            // console.log("Auto-play blocked (expected behavior). Audio will play after first user interaction.");
            // Keep isPlaying true so audio starts after user clicks/touches anything
          });
        hasAutoPlayedRef.current = true;
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOnMiniGamePath, IS_EVENT_NEW_YEAR_2025_THEME]);

  // Set up user interaction listeners - this should run on every render if user hasn't interacted
  useEffect(() => {
    if (userHasInteracted) return; // Don't set up listeners if user already interacted

    const unlockAudio = (event) => {
      // console.log("User interaction detected:", event.type);
      setUserHasInteracted(true);
      
      // Try to play immediately if conditions are met
      if (isPlaying && audioRef.current && IS_EVENT_NEW_YEAR_2025_THEME && isOnMiniGamePath) {
        audioRef.current.play().catch((error) => {
          // console.error("Error playing audio after interaction:", error);
        });
      }
    };

    // Add event listeners for user interaction
    events.forEach((event) => {
      document.addEventListener(event, unlockAudio, { once: true, passive: true });
    });

    return () => {
      // Clean up listeners
      events.forEach((event) => {
        document.removeEventListener(event, unlockAudio);
      });
    };
  }, [userHasInteracted, isPlaying, IS_EVENT_NEW_YEAR_2025_THEME, isOnMiniGamePath, events]);

  // Handle audio play/pause logic
  useEffect(() => {
    const audio = audioRef.current;

    if (!audio || !IS_EVENT_NEW_YEAR_2025_THEME || !isOnMiniGamePath) return;

    if (isPlaying && userHasInteracted) {
      // User wants audio playing and has interacted - safe to play
      audio.play().catch((error) => {
        // console.error("Error playing audio:", error);
        // If play fails, don't change the isPlaying state
        // User can try again by clicking the button
      });
    } else if (!isPlaying) {
      // User has muted, ensure audio is paused
      audio.pause();
    }
    // If isPlaying is true but userHasInteracted is false, do nothing
    // Wait for user interaction to trigger play
  }, [isPlaying, IS_EVENT_NEW_YEAR_2025_THEME, isOnMiniGamePath, userHasInteracted]);

  // Handle audio element loading
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleCanPlay = () => {
      // Only auto-play if user wants it and has interacted
      if (isPlaying && userHasInteracted && isOnMiniGamePath && IS_EVENT_NEW_YEAR_2025_THEME) {
        audio.play().catch((error) => {
          // console.log("Audio play failed:", error.message);
        });
      }
    };

    audio.addEventListener("canplay", handleCanPlay);
    
    return () => {
      audio.removeEventListener("canplay", handleCanPlay);
    };
  }, [isPlaying, userHasInteracted, isOnMiniGamePath, IS_EVENT_NEW_YEAR_2025_THEME]);

  const handlePlay = () => {
    if (audioRef.current) {
      setUserHasInteracted(true); // Mark interaction when user manually plays
      audioRef.current.play().catch((error) => {
        // console.error("Error playing audio:", error);
      });
      setIsPlaying(true);
      setStoredAudioSettings({ isPlaying: true });
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      setStoredAudioSettings({ isPlaying: false });
    }
  };

  const toggleAudio = () => {
    // console.log("Toggle audio - current isPlaying:", isPlaying);
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  };

  return (
    <NewYearAudioContext.Provider
      value={{
        isPlaying,
        setIsPlaying,
        audioRef,
        handlePlay,
        handlePause,
        toggleAudio,
        userHasInteracted, // Expose this for debugging
      }}
    >
      {children}
      {IS_EVENT_NEW_YEAR_2025_THEME && isOnMiniGamePath && (
        <audio
          ref={audioRef}
          src={newYearAudio}
          loop
          preload="auto"
          autoPlay
          style={{ display: "none" }}
        />
      )}
    </NewYearAudioContext.Provider>
  );
};

export const useNewYearAudioContext = () => {
  const context = useContext(NewYearAudioContext);
  if (!context) {
    // throw new Error(
    //   "useNewYearAudioContext must be used within NewYearAudioProvider"
    // );
    return null;
  }
  return context;
};